import { Fragment, useMemo, useRef, useState } from "react";
import {
  ClipboardIcon,
  CheckIcon,
  ChevronDownIcon,
  AdjustmentsVerticalIcon,
} from "@heroicons/react/24/outline";
import { Dialog, Popover, Transition } from "@headlessui/react";

import {
  useMeeting,
  usePubSub,
  useMediaDevice
} from "@videosdk.live/react-sdk";
import { gettext } from '@resuelve/malinchex';

import MicOnIcon from "../../icons/Bottombar/MicOnIcon";
import MicOffIcon from "../../icons/Bottombar/MicOffIcon";
import WebcamOnIcon from "../../icons/Bottombar/WebcamOnIcon";
import WebcamOffIcon from "../../icons/Bottombar/WebcamOffIcon";
import ScreenShareIcon from "../../icons/Bottombar/ScreenShareIcon";
import ChatIcon from "../../icons/Bottombar/ChatIcon";
import ParticipantsIcon from "../../icons/Bottombar/ParticipantsIcon";
import EndIcon from "../../icons/Bottombar/EndIcon";
import RaiseHandIcon from "../../icons/Bottombar/RaiseHandIcon";
import { OutlinedButton } from "../../components/buttons/OutlinedButton";
import useIsTab from "../../hooks/useIsTab";
import useMediaStream from "../../hooks/useMediaStream";
import useIsMobile from "../../hooks/useIsMobile";
import { MobileIconButton } from "../../components/buttons/MobileIconButton";
import ReactionIcon from "../../icons/Bottombar/ReactionIcon";
import { sideBarModes } from "../../utils/common";
import { useMeetingAppContext } from "../../MeetingAppContextDef";
import { i18nTexts } from "../../i18n";

export function ILSBottomBar({
  bottomBarHeight,
  setIsMeetingLeft
}) {
  const { sideBarMode, setSideBarMode } = useMeetingAppContext();
  const RaiseHandBTN = ({ isMobile, isTab }) => {
    const { publish } = usePubSub("RAISE_HAND");
    const RaiseHand = () => {
      publish("Raise Hand");
    };

    return isMobile || isTab ? (
      <MobileIconButton
        id="RaiseHandBTN"
        tooltipTitle={gettext(i18nTexts.meeting_screen.buttons.raise_hand_btn.tooltip)}
        Icon={RaiseHandIcon}
        onClick={RaiseHand}
        buttonText={gettext(i18nTexts.meeting_screen.buttons.raise_hand_btn.text)}
      />
    ) : (
      <OutlinedButton
        className="h-13 w-16"
        onClick={RaiseHand}
        tooltip={gettext(i18nTexts.meeting_screen.buttons.raise_hand_btn.tooltip)}
        Icon={RaiseHandIcon}
      />
    );
  };

  const MicBTN = () => {
    const {
      selectedMic,
      setSelectedMic,
      selectedSpeaker,
      setSelectedSpeaker,
      isMicrophonePermissionAllowed,
    } = useMeetingAppContext();
    const { getMicrophones, getPlaybackDevices } = useMediaDevice();

    const mMeeting = useMeeting();
    const [mics, setMics] = useState([]);
    const [speakers, setSpeakers] = useState([]);
    const localMicOn = mMeeting?.localMicOn;
    const changeMic = mMeeting?.changeMic;

    const onDeviceChanged = (devices) => {
      getMics();
      const newSpeakerList = devices.devices.filter(device => device.kind === 'audiooutput');

      if (newSpeakerList.length > 0) {
        setSelectedSpeaker({ id: newSpeakerList[0].deviceId, label: newSpeakerList[0].label });
      }
    }

    useMediaDevice({
      onDeviceChanged
    })

    const getMics = async () => {
      const mics = await getMicrophones();
      const speakers = await getPlaybackDevices();

      mics && mics?.length && setMics(mics);
      speakers && speakers?.length && setSpeakers(speakers);
    };

    return (
      <OutlinedButton
        className="md:h-13 md:w-16 mb-1 ml-1 mt-1 md:mb-2 md:ml-2 md:mt-2"
        hasSelector={true}
        Icon={localMicOn ? MicOnIcon : MicOffIcon}
        onClick={() => {
          mMeeting.toggleMic();
        }}
        bgColor={localMicOn ? "bg-gray-750" : "bg-white"}
        borderColor={localMicOn && "#ffffff33"}
        isFocused={localMicOn}
        focusIconColor={localMicOn && "white"}
        tooltip={localMicOn
          ? gettext(i18nTexts.meeting_screen.buttons.microphone_btn.tooltip.off)
          : gettext(i18nTexts.meeting_screen.buttons.microphone_btn.tooltip.on)
        }
        renderRightComponent={() => {
          return (
            <Popover className="relative flex">
              {({ close }) => (
                <>
                  <Popover.Button
                    disabled={!isMicrophonePermissionAllowed}
                    className={`justify-center rounded-r-lg border-2 border-solid border-l-0
                                    mb-1 mr-1 mt-1 md:mb-2 md:mr-2 md:mt-2 p-1
                                    ${localMicOn ? "bg-gray-700" : "bg-gray-750"}`}
                    style={{
                      borderColor: localMicOn ? "#ffffff33" : "bg-gray-750"
                    }}
                    onClick={getMics}
                  >
                    <ChevronDownIcon
                      className="h-4 w-4"
                      style={{
                        color: "white"
                      }}
                    />
                  </Popover.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 translate-y-1"
                  >
                    <Popover.Panel className="absolute left-1/2 bottom-full z-10 mt-3 w-72 -translate-x-1/2 transform px-4 sm:px-0 pb-4">
                      <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                        <div className="bg-gray-750 py-1">
                          <div>
                            <div className="flex items-center p-3 pb-0">
                              <p className="ml-3 text-sm text-gray-900">
                                {gettext(i18nTexts.meeting_screen.buttons.microphone_btn.selector)?.toUpperCase()}
                              </p>
                            </div>
                            <div className="flex flex-col">
                              {mics.map(({ deviceId, label }, index) => (
                                <div
                                  key={`mics_${deviceId}`}
                                  className={`px-3 py-1 my-1 pl-6 text-white text-left ${deviceId === selectedMic.id &&
                                    "bg-gray-150"
                                    }`}
                                >
                                  <button
                                    className={`flex flex-1 w-full ${deviceId === selectedMic.id &&
                                      "bg-gray-150"
                                      }`}
                                    key={`mics_${deviceId}`}
                                    onClick={() => {
                                      setSelectedMic({ id: deviceId });
                                      changeMic(deviceId);
                                      close();
                                    }}
                                  >
                                    {label || `Mic ${index + 1}`}
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                          <hr className="border border-gray-50 mt-2 mb-1" />
                          <div>
                            <div className="flex p-3 pb-0">
                              <p className="ml-3 text-sm text-gray-900  text-center">
                                {"SPEAKER"}
                              </p>
                            </div>
                            <div className="flex flex-col ">
                              {speakers.map(({ deviceId, label }, index) => (
                                <div
                                  className={`px-3 py-1 my-1 pl-6 text-white ${deviceId === selectedSpeaker.id &&
                                    "bg-gray-150"
                                    }`}
                                >
                                  <button
                                    className={`flex flex-1 w-full text-left ${deviceId === selectedSpeaker.id &&
                                      "bg-gray-150"
                                      }`}
                                    key={`speakers_${deviceId}`}
                                    onClick={() => {
                                      setSelectedSpeaker({ id: deviceId });
                                      close();
                                    }}
                                  >
                                    {label || `Speaker ${index + 1}`}
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </Popover.Panel>
                  </Transition>
                </>
              )}
            </Popover>
          );
        }}
      />
    );
  };

  const WebCamBTN = () => {
    const { selectedWebcam, setSelectedWebcam, isCameraPermissionAllowed } = useMeetingAppContext();

    const { getCameras } = useMediaDevice();
    const mMeeting = useMeeting();
    const [webcams, setWebcams] = useState([]);
    const { getVideoTrack } = useMediaStream();

    const localWebcamOn = mMeeting?.localWebcamOn;
    const changeWebcam = mMeeting?.changeWebcam;

    const getWebcams = async () => {
      let webcams = await getCameras();
      webcams && webcams?.length && setWebcams(webcams);
    };

    return (
      <OutlinedButton
        className="md:h-13 md:w-16 mb-1 ml-1 mt-1 md:mb-2 md:ml-2 md:mt-2"
        hasSelector={true}
        Icon={localWebcamOn ? WebcamOnIcon : WebcamOffIcon}
        onClick={async () => {
          let track;
          if (!localWebcamOn) {
            track = await getVideoTrack({
              webcamId: selectedWebcam.id,
            });

            if (track) {
              mMeeting.startWebcam(track);
            }
          } else {
            mMeeting.toggleWebcam();
          }
        }}
        bgColor={localWebcamOn ? "bg-gray-750" : "bg-white"}
        borderColor={localWebcamOn && "#ffffff33"}
        isFocused={localWebcamOn}
        focusIconColor={localWebcamOn && "white"}
        tooltip={localWebcamOn
          ? gettext(i18nTexts.meeting_screen.buttons.webcam_btn.tooltip.off)
          : gettext(i18nTexts.meeting_screen.buttons.webcam_btn.tooltip.on)
        }
        renderRightComponent={() => {
          return (
            <Popover className="relative flex">
              {({ close }) => (
                <>
                  <Popover.Button
                    disabled={!isCameraPermissionAllowed}
                    className={`justify-center rounded-r-lg border-2 border-solid border-l-0
                                    mb-1 mr-1 mt-1 md:mb-2 md:mr-2 md:mt-2 p-1
                                    ${localWebcamOn ? "bg-gray-700" : "bg-gray-750"}`}
                    style={{
                      borderColor: localWebcamOn ? "#ffffff33" : "bg-gray-750"
                    }}
                    onClick={getWebcams}
                  >
                    <ChevronDownIcon
                      className="h-4 w-4"
                      style={{
                        color: "white"
                      }}
                    />
                  </Popover.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 translate-y-1"
                  >
                    <Popover.Panel className="absolute left-1/2 bottom-full z-10 mt-3 w-72 -translate-x-1/2 transform px-4 sm:px-0 pb-4">
                      <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                        <div className="bg-gray-750 py-1">
                          <div>
                            <div className="flex items-center p-3 pb-0">
                              <p className="ml-3 text-sm text-gray-900">
                                {gettext(i18nTexts.meeting_screen.buttons.webcam_btn.selector)?.toUpperCase()}
                              </p>
                            </div>
                            <div className="flex flex-col">
                              {webcams.map(({ deviceId, label }, index) => (
                                <div
                                  key={`output_webcams_${deviceId}`}
                                  className={`px-3 py-1 my-1 pl-6 text-white text-left ${deviceId === selectedWebcam.id &&
                                    "bg-gray-150"
                                    }`}
                                >
                                  <button
                                    className={`flex flex-1 w-full ${deviceId === selectedWebcam.id &&
                                      "bg-gray-150"
                                      }`}
                                    key={`output_webcams_${deviceId}`}
                                    onClick={() => {
                                      setSelectedWebcam({ id: deviceId });
                                      changeWebcam(deviceId);
                                      close();
                                    }}
                                  >
                                    {label || `Webcam ${index + 1}`}
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </Popover.Panel>
                  </Transition>
                </>
              )}
            </Popover>
          );
        }}
      />
    );
  };

  const ScreenShareBTN = ({ isMobile, isTab }) => {
    const { localScreenShareOn, toggleScreenShare, presenterId } = useMeeting();

    return isMobile || isTab ? (
      <MobileIconButton
        id="screen-share-btn"
        tooltipTitle={
          presenterId
            ? localScreenShareOn
              ? gettext(i18nTexts.meeting_screen.buttons.screen_share_btn.tooltip.stop)
              : null
            : gettext(i18nTexts.meeting_screen.buttons.screen_share_btn.tooltip.present)
        }
        buttonText={
          presenterId
            ? localScreenShareOn
              ? gettext(i18nTexts.meeting_screen.buttons.screen_share_btn.tooltip.stop)
              : null
            : gettext(i18nTexts.meeting_screen.buttons.screen_share_btn.tooltip.present)
        }
        isFocused={localScreenShareOn}
        Icon={ScreenShareIcon}
        onClick={() => toggleScreenShare()}
        disabled={presenterId ? !localScreenShareOn : false}
      />
    ) : (
      <OutlinedButton
        className="h-13 w-16"
        Icon={ScreenShareIcon}
        onClick={() => toggleScreenShare()}
        isFocused={localScreenShareOn}
        tooltip={
          presenterId
            ? localScreenShareOn
              ? gettext(i18nTexts.meeting_screen.buttons.screen_share_btn.tooltip.stop)
              : null
            : gettext(i18nTexts.meeting_screen.buttons.screen_share_btn.tooltip.present)
        }
        disabled={presenterId ? (localScreenShareOn ? false : true) : false}
      />
    );
  };

  const LeaveBTN = () => {
    const { leave } = useMeeting();

    return (
      <OutlinedButton
        className="md:h-13 md:w-16"
        Icon={EndIcon}
        bgColor="bg-red-700"
        onClick={() => {
          leave();
          setIsMeetingLeft(true);
        }}
        tooltip={gettext(i18nTexts.meeting_screen.buttons.leave_btn.tooltip)}
      />
    );
  };

  const ChatBTN = ({ isMobile, isTab }) => {
    return isMobile || isTab ? (
      <MobileIconButton
        tooltipTitle={gettext(i18nTexts.meeting_screen.buttons.chat_btn.tooltip)}
        buttonText={gettext(i18nTexts.meeting_screen.buttons.chat_btn.text)}
        Icon={ChatIcon}
        isFocused={sideBarMode === sideBarModes.CHAT}
        onClick={() => {
          setSideBarMode((s) =>
            s === sideBarModes.CHAT ? null : sideBarModes.CHAT
          );
        }}
      />
    ) : (
      <OutlinedButton
        className="h-13 w-16"
        Icon={ChatIcon}
        onClick={() => {
          setSideBarMode((s) =>
            s === sideBarModes.CHAT ? null : sideBarModes.CHAT
          );
        }}
        isFocused={sideBarMode === "Chat"}
        tooltip={gettext(i18nTexts.meeting_screen.buttons.chat_btn.tooltip)}
      />
    );
  };

  const ParticipantsBTN = ({ isMobile, isTab }) => {
    const { participants } = useMeeting();

    return isMobile || isTab ? (
      <MobileIconButton
        tooltipTitle={gettext(i18nTexts.meeting_screen.buttons.participants_btn.tooltip)}
        isFocused={sideBarMode === sideBarModes.PARTICIPANTS}
        buttonText={gettext(i18nTexts.meeting_screen.buttons.participants_btn.text)}
        disabledOpacity={1}
        Icon={ParticipantsIcon}
        onClick={() => {
          setSideBarMode((s) =>
            s === sideBarModes.PARTICIPANTS ? null : sideBarModes.PARTICIPANTS
          );
        }}
        badge={`${new Map(participants)?.size}`}
      />
    ) : (
      <OutlinedButton
        className="h-13 w-16"
        Icon={ParticipantsIcon}
        onClick={() => {
          setSideBarMode((s) =>
            s === sideBarModes.PARTICIPANTS ? null : sideBarModes.PARTICIPANTS
          );
        }}
        isFocused={sideBarMode === sideBarModes.PARTICIPANTS}
        tooltip={gettext(i18nTexts.meeting_screen.buttons.participants_btn.tooltip)}
        badge={`${new Map(participants)?.size}`}
      />
    );
  };

  const MeetingIdCopyBTN = () => {
    const { meetingId } = useMeeting();
    const [isCopied, setIsCopied] = useState(false);
    return (
      <div className="flex items-center justify-center lg:ml-0 ml-4 mt-4 xl:mt-0">
        <div className="flex border-2 border-gray-850 p-2 rounded-md items-center justify-center">
          <h1 className="text-white text-base ">{meetingId}</h1>
          <button
            className="ml-2"
            onClick={() => {
              navigator.clipboard.writeText(meetingId);
              setIsCopied(true);
              setTimeout(() => {
                setIsCopied(false);
              }, 3000);
            }}
          >
            {isCopied ? (
              <CheckIcon className="h-5 w-5 text-green-400" />
            ) : (
              <ClipboardIcon className="h-5 w-5 text-white" />
            )}
          </button>
        </div>
      </div>
    );
  };

  const tollTipEl = useRef();
  const isMobile = useIsMobile();
  const isTab = useIsTab();
  const [open, setOpen] = useState(false);

  const handleClickFAB = () => {
    setOpen(true);
  };

  const handleCloseFAB = () => {
    setOpen(false);
  };

  const BottomBarButtonTypes = useMemo(
    () => ({
      END_CALL: "END_CALL",
      CHAT: "CHAT",
      PARTICIPANTS: "PARTICIPANTS",
      SCREEN_SHARE: "SCREEN_SHARE",
      WEBCAM: "WEBCAM",
      MIC: "MIC",
      RAISE_HAND: "RAISE_HAND",
      RECORDING: "RECORDING",
      MEETING_ID_COPY: "MEETING_ID_COPY"
    }),
    []
  );

  const otherFeatures = [
    { icon: BottomBarButtonTypes.RAISE_HAND },
    { icon: BottomBarButtonTypes.SCREEN_SHARE },
    { icon: BottomBarButtonTypes.CHAT },
    { icon: BottomBarButtonTypes.PARTICIPANTS },
    { icon: BottomBarButtonTypes.MEETING_ID_COPY }
  ];

  return isMobile || isTab ? (
    <div
      className="flex items-center justify-center"
      style={{ height: bottomBarHeight }}
    >
      <MicBTN />
      <WebCamBTN />
      <OutlinedButton Icon={AdjustmentsVerticalIcon} onClick={handleClickFAB} />
      <LeaveBTN />

      <Transition appear show={Boolean(open)} as={Fragment}>
        <Dialog
          as="div"
          className="relative"
          style={{ zIndex: 9999 }}
          onClose={handleCloseFAB}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="translate-y-full opacity-0 scale-95"
            enterTo="translate-y-0 opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="translate-y-0 opacity-100 scale-100"
            leaveTo="translate-y-full opacity-0 scale-95"
          >
            <div className="fixed inset-0 overflow-y-hidden">
              <div className="flex h-full items-end justify-end text-center">
                <Dialog.Panel className="w-screen transform overflow-hidden bg-gray-750 shadow-xl transition-all">
                  <div className="grid container bg-gray-750 py-6">
                    <div className="grid grid-cols-12 gap-2">
                      {otherFeatures.map(({ icon }, index) => {
                        return (
                          <div
                            key={`other-features-${icon}-${index}`}
                            className={`grid items-center justify-center ${icon === BottomBarButtonTypes.MEETING_ID_COPY
                              ? "col-span-7 sm:col-span-5 md:col-span-3"
                              : "col-span-4 sm:col-span-3 md:col-span-2"
                              }`}
                          >
                            {icon === BottomBarButtonTypes.RAISE_HAND ? (
                              <RaiseHandBTN isMobile={isMobile} isTab={isTab} />
                            ) : icon === BottomBarButtonTypes.SCREEN_SHARE ? (
                              <ScreenShareBTN isMobile={isMobile} isTab={isTab} />
                            ) : icon === BottomBarButtonTypes.CHAT ? (
                              <ChatBTN isMobile={isMobile} isTab={isTab} />
                            ) : icon === BottomBarButtonTypes.PARTICIPANTS ? (
                              <ParticipantsBTN
                                isMobile={isMobile}
                                isTab={isTab}
                              />
                            ) : icon ===
                              BottomBarButtonTypes.MEETING_ID_COPY ? (
                              <MeetingIdCopyBTN
                                isMobile={isMobile}
                                isTab={isTab}
                              />
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </Dialog.Panel>
              </div>
            </div>
          </Transition.Child>
        </Dialog>
      </Transition>
    </div>
  ) : (
    <div className="md:flex lg:px-2 xl:px-6 pb-2 px-2 hidden z-10">
      <MeetingIdCopyBTN />

      <div className="flex flex-1 items-center justify-center" ref={tollTipEl}>
        <MicBTN />
        <WebCamBTN />
        <RaiseHandBTN isMobile={isMobile} isTab={isTab} />
        <ScreenShareBTN isMobile={isMobile} isTab={isTab} />
        <LeaveBTN />
      </div>
      <div className="flex items-center justify-center">
        <ChatBTN isMobile={isMobile} isTab={isTab} />
        <ParticipantsBTN isMobile={isMobile} isTab={isTab} />
      </div>
    </div>
  );
}


export const ReactionBTN = ({ isMobile, isTab }) => {
  const [btnClicked, setBtnClicked] = useState(false);
  const { publish } = usePubSub("REACTION");

  const handleOpenMenu = (event) => {
    setBtnClicked(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setBtnClicked(null);
  };

  function sendEmoji(emoji) {
    // Dispatch custom event here so the local user can see their own emoji
    window.dispatchEvent(
      new CustomEvent("reaction_added", { detail: { emoji } })
    );
    handleCloseMenu();
  }

  const emojiArray = [
    { emoji: "😍", emojiName: "heartEye" },
    { emoji: "😂", emojiName: "laugh" },
    { emoji: "👍", emojiName: "thumbsup" },
    { emoji: "🎉", emojiName: "confetti" },
    { emoji: "👏", emojiName: "clap" },
    { emoji: "❤️", emojiName: "heart" },
  ];

  return (
    <div>
      <Popover className="relative">
        {({ open }) => (
          <>
            <Popover.Button>
              <OutlinedButton
                className="h-13 w-20"
                Icon={ReactionIcon}
                onClick={(e) => {
                  handleOpenMenu(e);
                }}
                isFocused={btnClicked}
                tooltip={"Reactions"}
              />
            </Popover.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute left-1/2 bottom-full z-10 mt-3  -translate-x-1/2 transform px-4 sm:px-0 ">
                <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                  <div className="px-1 py-2 bg-white rounded-md flex">
                    {emojiArray.map(({ emoji, emojiName }) => (
                      <button
                        key={`reaction-${emojiName}`}
                        className="mx-2"
                        onClick={() => {
                          sendEmoji(emojiName);
                          publish(emojiName);
                        }}
                      >
                        <p className="text-3xl">{emoji}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  );
};